import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * CLM-51 — the nav rail's per-surface information affordance.
 *
 * COPY SOURCE. The text this renders is `SurfaceDefinition.summary` from
 * src/routes/surfaces.ts, passed in verbatim. That table is the single home of
 * every surface's one-line purpose (it is also what SurfaceStub prints), so
 * this component never holds a string of its own — a second copy would drift
 * the moment either side was edited.
 *
 * THE PATTERN IS AN ARIA TOOLTIP, NOT A DISCLOSURE. `role="tooltip"` plus
 * `aria-describedby` on the trigger; deliberately no `aria-expanded`, no
 * `aria-haspopup` and no `title`. Two consequences are load-bearing:
 *
 *   1. `aria-describedby` is emitted UNCONDITIONALLY and the bubble node is
 *      always in the DOM, carrying the `hidden` attribute while closed. Per the
 *      accname spec, text reached *through* `aria-describedby` is exposed even
 *      from a hidden element, so the description is computed the instant focus
 *      lands rather than racing a React re-render, and the idref is never
 *      dangling. Only *visibility* is stateful.
 *
 *   2. ESCAPE IS HANDLED ON `document`, NOT ON THE TRIGGER. The pointer path
 *      opens the bubble while focus sits somewhere else entirely (the body, the
 *      previous link, the search box); a trigger-local keydown handler would
 *      never see that Escape and the bubble would be undismissable by keyboard
 *      — a direct WCAG 1.4.13 *Dismissible* failure. The document listener
 *      closes it and touches focus in no way, which is exactly what "without
 *      moving pointer hover or keyboard focus" asks for. It does not
 *      `stopPropagation()`: this component does not own the key, and swallowing
 *      it would hide Escape from any later shell consumer (a modal, say).
 *
 * THE TRIGGER IS A SIBLING OF THE <a>, NEVER A CHILD. A button inside an anchor
 * is invalid HTML and would make the icon a second way to navigate; as a
 * sibling, activating it cannot change the route.
 *
 * Interaction *styling* is CSS (`:hover`, `:focus-visible`) — see
 * src/styles/shell.css. React state here carries only whether the bubble
 * exists on screen.
 */

export interface SurfaceInfoProps {
  /** Surface id from surfaces.ts; forms the tooltip id `nav-info-tip-<surface_id>`. */
  readonly surface_id: string;
  /** Surface label, used only for the accessible name "About <label>". */
  readonly label: string;
  /** The surface's own `summary`, rendered verbatim. */
  readonly summary: string;
}

type Placement = 'right' | 'below';

interface BubblePosition {
  readonly top: number;
  readonly left: number;
  readonly place: Placement;
}

/**
 * Kept in step with `.navtip { max-width: 260px }` in shell.css. It appears
 * here because flip detection is arithmetic on the viewport, which CSS cannot
 * express; the bubble's *appearance* still carries no measurement in this file.
 */
const BUBBLE_MAX_WIDTH = 260;

/** The corridor between trigger and bubble, matched by `.navtip::before`. */
const GAP = 8;

/** Minimum breathing room from the viewport edge when the bubble flips below. */
const EDGE = 8;

/**
 * How long the bubble survives the pointer leaving the trigger. Long enough to
 * travel the 8px corridor onto the bubble itself, which is what makes the
 * bubble hoverable at all; the bubble's own mouseenter then cancels the hide.
 */
const HIDE_DELAY_MS = 120;

/**
 * Right of the trigger, top-aligned — or below it when the bubble would leave
 * the viewport.
 *
 * TWO PLACEMENTS ONLY, `right` AND `below`. Never `left`: the rail is flush to
 * the left edge of the app, so a left flip always lands the bubble *on the
 * rail*, and in the light theme the rail (`--surface-2`), the bubble background
 * (`--page`) and the trigger's hover fill (`--surface-3`) all resolve to the
 * same #f5f5f5 — the bubble would have no surface of its own, only its 1px
 * border. Below keeps it over the content area instead.
 *
 * No `transform` is used to place it: a transform on the bubble or an ancestor
 * would create a fixed containing block and re-introduce the `.app { overflow:
 * hidden }` clipping that `position: fixed` exists here to escape.
 */
function placeBubble(rect: DOMRect, viewportWidth: number): BubblePosition {
  if (rect.right + GAP + BUBBLE_MAX_WIDTH <= viewportWidth) {
    return { top: rect.top, left: rect.right + GAP, place: 'right' };
  }

  const maxLeft = viewportWidth - BUBBLE_MAX_WIDTH - EDGE;
  return {
    top: rect.bottom + GAP,
    left: Math.max(EDGE, Math.min(rect.left, maxLeft)),
    place: 'below',
  };
}

export function SurfaceInfo({ surface_id, label, summary }: SurfaceInfoProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<BubblePosition | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  /**
   * The pointer device currently on this trigger, `''` once it has left.
   *
   * A FALLBACK, NOT THE SOURCE OF TRUTH. The click handler reads the device off
   * the click event itself, because a ref that merely remembers the last device
   * seen latches to `'mouse'` for the life of the component and then swallows
   * every keyboard activation that follows. This ref is consulted only to tell
   * mouse from touch when the click carries no `pointerType` of its own (jsdom,
   * and engines that still dispatch `click` as a plain MouseEvent), and it is
   * cleared on `pointerleave` and on `blur` so it cannot go stale.
   */
  const pointerTypeRef = useRef<string>('');

  /**
   * Set by Escape, cleared by `pointerleave`, `blur` and a fresh click. Without
   * it, an Escape-dismissed bubble springs straight back on the next
   * `mousemove` inside the trigger — the bubble would be undismissable in
   * practice even though the key "worked".
   */
  const dismissedRef = useRef(false);

  /**
   * True while a pointer press on this trigger is in flight — from
   * `pointerdown` until the `click` it produces. Not cleared on `pointerup`:
   * on a touch tap the browser (and user-event) focuses the button *after*
   * pointerup, and it is that focus this ref has to be readable from. A press
   * that never reaches a click (dragged off the button) is cleared by `blur`
   * or by the mouse `pointerleave`.
   */
  const pressRef = useRef(false);

  /**
   * True while the bubble's open state was established by the focus that THIS
   * pointer press caused.
   *
   * PRESSING A BUTTON FOCUSES IT — on a phone as much as with a mouse. So a tap
   * arrives as pointerdown, pointerup, focus, click (the focus lands after
   * pointerup, which is why `pressRef` outlives it), and the focus half opens
   * the bubble before the click is seen. Without this ref the click would then
   * toggle it straight back closed and the info icon would do nothing at all on
   * a coarse pointer: the same failure the pointer-device guard below prevents
   * on the hover path, reached through focus instead.
   *
   * ITS LIFE IS ONE ACTIVATION. It is armed only while `pressRef` says a
   * pointer press caused the focus, and the click it excuses clears it again.
   * Arming it on *every* focus made it permanent for a keyboard user: Tab
   * opened the bubble and Enter/Space could then never toggle it, which
   * contradicts §3 of the spec ("a keyboard Enter/Space activation also
   * toggles"). Also cleared on `blur` and by Escape.
   */
  const focusOpenedRef = useRef(false);

  const clearHide = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const openBubble = useCallback(() => {
    clearHide();
    dismissedRef.current = false;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos(placeBubble(rect, window.innerWidth));
    setOpen(true);
  }, [clearHide]);

  const closeBubble = useCallback(() => {
    clearHide();
    setOpen(false);
  }, [clearHide]);

  const scheduleHide = useCallback(() => {
    clearHide();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      // A click focuses the trigger, so on a mouse the pointer can leave while
      // the trigger still holds focus. The focus trigger has not been removed,
      // so the description must stay up (checklist 6/9: visible until the hover
      // OR focus trigger goes away); `blur` is the closer for that state.
      if (document.activeElement === triggerRef.current) return;
      setOpen(false);
    }, HIDE_DELAY_MS);
  }, [clearHide]);

  // Global listeners exist ONLY while a bubble is open, and are the whole of
  // this feature's global footprint. Escape and outside-pointerdown are the two
  // dismissals WCAG 1.4.13 and the touch path need; scroll and resize close
  // rather than reposition, because positions are computed once on open.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Close, and nothing else. Never blur(), never focus(), never
      // preventDefault() on a key this component does not own.
      clearHide();
      dismissedRef.current = true;
      focusOpenedRef.current = false;
      setOpen(false);
    };

    const onPointerDownOutside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (triggerRef.current?.contains(target)) return;
      if (tipRef.current?.contains(target)) return;
      clearHide();
      setOpen(false);
    };

    const onViewportChange = () => {
      clearHide();
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDownOutside);
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDownOutside);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [open, clearHide]);

  // A pending hide must not outlive the component.
  useEffect(() => clearHide, [clearHide]);

  const tooltipId = `nav-info-tip-${surface_id}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="navinfo"
        // Unconditional, and pointing at a node that never leaves the DOM.
        aria-describedby={tooltipId}
        onFocus={() => {
          // Armed only for the focus a pointer press caused. A Tab-focus must
          // leave the Enter/Space that follows free to toggle (spec §3).
          focusOpenedRef.current = pressRef.current;
          openBubble();
        }}
        onBlur={() => {
          dismissedRef.current = false;
          focusOpenedRef.current = false;
          pressRef.current = false;
          pointerTypeRef.current = '';
          closeBubble();
        }}
        onPointerDown={(event) => {
          pointerTypeRef.current = event.pointerType;
          pressRef.current = true;
          // A new press. Whatever focus did last time no longer excuses the
          // click that is about to arrive.
          focusOpenedRef.current = false;
        }}
        onPointerEnter={(event) => {
          pointerTypeRef.current = event.pointerType;
          // Mouse only. A touch tap also fires pointerenter, and opening there
          // is precisely what makes the icon do nothing on a phone: the
          // synthesized enter would open and the click that follows would
          // immediately close.
          if (event.pointerType !== 'mouse') return;
          if (dismissedRef.current) return;
          openBubble();
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') return;
          pointerTypeRef.current = '';
          // A press dragged off the button produces no click to clear it.
          pressRef.current = false;
          dismissedRef.current = false;
          scheduleHide();
        }}
        onClick={(event) => {
          event.preventDefault();

          // The activation ends here, so both one-activation refs are read into
          // locals and cleared immediately: whatever this click decides, it must
          // not carry over to the next Enter, tap or press.
          const from_press_focus = focusOpenedRef.current;
          focusOpenedRef.current = false;
          pressRef.current = false;

          // THE DEVICE IS READ OFF THIS EVENT, never off a ref remembering the
          // last one seen: a latched `'mouse'` swallows every keyboard
          // activation for the life of the component. A keyboard-generated
          // click carries no click count (`detail === 0`) and, where the engine
          // dispatches `click` as a PointerEvent, an empty `pointerType`; jsdom
          // reports an empty `pointerType` for *every* click, which is why
          // `detail` decides keyboard-or-pointer and the ref is consulted only
          // as the mouse/touch fallback.
          const native = event.nativeEvent as MouseEvent & { pointerType?: string };
          const from_keyboard = event.detail === 0;
          const pointer_type = native.pointerType || pointerTypeRef.current;

          // On a mouse the hover already opened it, so the click is a no-op
          // toggle guard rather than a close.
          if (!from_keyboard && pointer_type === 'mouse') return;
          // And if the focus this very press caused is what opened it, the tap
          // has already done its job — toggling here would close it again.
          if (from_press_focus) return;
          dismissedRef.current = false;
          if (open) {
            closeBubble();
          } else {
            openBubble();
          }
        }}
      >
        <svg
          className="navinfo-i"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 7.25v4" />
          <path d="M8 4.75h.01" />
        </svg>
        {/* Visually hidden TEXT, not aria-label: it stays translatable with the
            rest of the shell, and it is what gives the icon-only control a
            non-empty accessible name. */}
        <span className="vh">About {label}</span>
      </button>

      {/* Always rendered. `hidden` is what hides it — never a class that sets
          `display`, and never conditional JSX, because the node must exist
          while closed for aria-describedby to resolve. */}
      <div
        ref={tipRef}
        id={tooltipId}
        className="navtip"
        role="tooltip"
        hidden={!open}
        data-place={pos?.place ?? 'right'}
        style={pos ? { top: pos.top, left: pos.left } : undefined}
        onMouseEnter={clearHide}
        onMouseLeave={scheduleHide}
      >
        {summary}
      </div>
    </>
  );
}
